"use client"

import * as React from "react"
import { Autocomplete as BaseAutocomplete } from "@base-ui/react/autocomplete"
import { RiArrowDownSFill, RiCloseFill } from "@remixicon/react"
import { AccessibleIcon } from "@/components/ui/AccessibleIcon"

import { cn } from "@/lib/utils"

import { focusRing } from "./shared/component-state"

export type EventDetails = Parameters<
  NonNullable<
    React.ComponentProps<typeof BaseAutocomplete.Root>["onValueChange"]
  >
>

export interface AutocompleteProps<
  TValue extends readonly { items: readonly any[] }[] = readonly {
    items: readonly any[]
  }[],
> extends Omit<
  React.ComponentProps<typeof BaseAutocomplete.Root<TValue>>,
  // "children" | "multiple" | "onValueChange"
  "children" | "multiple"
> {
  empty?: React.ComponentProps<typeof BaseAutocomplete.Empty>["children"]
  status?: React.ComponentProps<typeof BaseAutocomplete.Status>["children"]
  placeholder?: React.ComponentProps<
    typeof BaseAutocomplete.Input
  >["placeholder"]
  ref?: React.ComponentProps<typeof BaseAutocomplete.Input>["ref"]
  type?: React.ComponentProps<typeof BaseAutocomplete.Input>["type"]
  children?: React.ComponentProps<typeof BaseAutocomplete.List>["children"]
  className?: string
  // onValueChange?: (value: TValue, eventDetails: EventDetails) => void;
}

/**
 * Root component for the Autocomplete.
 * See @base-ui/react/autocomplete for available props.
 *
 * `IMPORTANT`: multiple is not implemented for this usecase. Follow
 * The individual setup at base-ui.
 */
export function Autocomplete<
  TValue extends readonly { items: readonly any[] }[] = readonly {
    items: readonly any[]
  }[],
>({
  placeholder,
  empty = "No results found.",
  status,
  children,
  className,
  id,
  ref,
  type,
  ...props
}: AutocompleteProps<TValue>) {
  return (
    <AutocompleteRoot {...props}>
      <span className={cn("relative w-full", className)}>
        <AutocompleteClear />
        <AutocompleteTrigger />
        <AutocompleteInput
          placeholder={placeholder}
          type={type}
          ref={ref}
          id={id}
        />
      </span>

      <AutocompletePortal>
        <AutocompletePositioner sideOffset={8}>
          <AutocompletePopup>
            <AutocompleteStatus>{status}</AutocompleteStatus>
            <AutocompleteEmpty>{empty}</AutocompleteEmpty>
            <AutocompleteList>{children}</AutocompleteList>
          </AutocompletePopup>
        </AutocompletePositioner>
      </AutocompletePortal>
    </AutocompleteRoot>
  )
}

// Root Autocomplete component
export function AutocompleteRoot<
  TValue extends readonly { items: readonly any[] }[] = readonly {
    items: readonly any[]
  }[],
>({
  children,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Root<TValue>>) {
  return <BaseAutocomplete.Root {...props}>{children}</BaseAutocomplete.Root>
}

// Input for Autocomplete
export function AutocompleteInput({
  className,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Input>) {
  return (
    <BaseAutocomplete.Input
      className={cn(
        "border-input text-foreground data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-input/30 hover:bg-input/50 flex w-fit items-center justify-between gap-2 rounded-md border px-4 py-2 text-base whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "bg-input hover:bg-input-accent dark:bg-input/30 dark:hover:bg-input-accent border-border/60",
        "w-full pr-16 truncate",
        focusRing,
        className
      )}
      {...props}
    />
  )
}

// Trigger button for Autocomplete
export function AutocompleteTrigger({
  className,
  children = <AccessibleIcon Icon={RiArrowDownSFill} decorative color="currentColor" className="size-4 opacity-50" />,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Trigger>) {
  return (
    <BaseAutocomplete.Trigger
      className={cn(
        "touch-hitbox absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-md p-1",
        "text-muted-foreground hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring/50 transition-colors",
        className
      )}
      {...props}
    >
      {children}
    </BaseAutocomplete.Trigger>
  )
}

// Icon for Autocomplete (used in trigger)
export function AutocompleteIcon({
  className,
  children,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Icon>) {
  return (
    <BaseAutocomplete.Icon
      className={cn("flex items-center", className)}
      {...props}
    >
      {children}
    </BaseAutocomplete.Icon>
  )
}

// Clear button for Autocomplete
export function AutocompleteClear({
  className,
  children = <AccessibleIcon Icon={RiCloseFill} decorative color="currentColor" className="size-4 opacity-50" />,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Clear>) {
  return (
    <BaseAutocomplete.Clear
      className={cn(
        "touch-hitbox absolute right-10 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-md p-1",
        "text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 transition-colors",
        className
      )}
      {...props}
    >
      {children}
    </BaseAutocomplete.Clear>
  )
}

// Value display for Autocomplete
export function AutocompleteValue(
  props: React.ComponentProps<typeof BaseAutocomplete.Value>
) {
  return <BaseAutocomplete.Value {...props} />
}

// Portal for Autocomplete popup
export function AutocompletePortal(
  props: React.ComponentProps<typeof BaseAutocomplete.Portal>
) {
  return <BaseAutocomplete.Portal {...props} />
}

// Backdrop for Autocomplete popup
export function AutocompleteBackdrop(
  props: React.ComponentProps<typeof BaseAutocomplete.Backdrop>
) {
  return <BaseAutocomplete.Backdrop {...props} />
}

// Positioner for Autocomplete popup
export function AutocompletePositioner({
  className,
  sideOffset = 4,
  alignOffset = 4,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Positioner>) {
  return (
    <BaseAutocomplete.Positioner
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      className={cn("outline-none select-none z-10", className)}
      {...props}
    />
  )
}

// Popup container for Autocomplete list
export function AutocompletePopup({
  className,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Popup>) {
  return (
    <BaseAutocomplete.Popup
      className={cn(
        "w-[var(--anchor-width)] max-h-[min(var(--available-height),23rem)] max-w-[var(--available-width)] origin-[var(--transform-origin)] overflow-y-auto scroll-pt-2 scroll-pb-2 overscroll-contain bg-popover py-2 text-popover-foreground shadow-lg transition-[transform,scale,opacity] data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[side=none]:data-[ending-style]:transition-none data-[starting-style]:scale-95 data-[starting-style]:opacity-0 data-[side=none]:data-[starting-style]:scale-100 data-[side=none]:data-[starting-style]:opacity-100 data-[side=none]:data-[starting-style]:transition-none dark:shadow-none dark:-outline-offset-1 dark:outline-gray-300",
        "rounded-lg bg-clip-padding border border-border transition-[transform,scale,opacity]",
        className
      )}
      {...props}
    />
  )
}

// Arrow indicator for Autocomplete popup
export function AutocompleteArrow(
  props: React.ComponentProps<typeof BaseAutocomplete.Arrow>
) {
  return <BaseAutocomplete.Arrow {...props} />
}

// Status message (e.g. loading, error)
export function AutocompleteStatus({
  className,
  children,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Status>) {
  return (
    <BaseAutocomplete.Status
      className={cn(
        "px-4 py-2 text-[0.925rem] leading-4 text-muted-foreground empty:m-0 empty:p-0",
        className
      )}
      {...props}
    >
      {children}
    </BaseAutocomplete.Status>
  )
}

// Empty state for Autocomplete
export function AutocompleteEmpty({
  className,
  children,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Empty>) {
  return (
    <BaseAutocomplete.Empty
      className={cn(
        "px-4 py-2 text-[0.925rem] leading-4 text-muted-foreground empty:m-0 empty:p-0",
        className
      )}
      {...props}
    >
      {children}
    </BaseAutocomplete.Empty>
  )
}

// List container for Autocomplete items
export function AutocompleteList({
  className,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.List>) {
  return <BaseAutocomplete.List className={cn("", className)} {...props} />
}

// Row for Autocomplete item (for virtualization, etc)
export function AutocompleteRow({
  className,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Row>) {
  return <BaseAutocomplete.Row className={className} {...props} />
}

// Single item in Autocomplete list
export function AutocompleteItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Item>) {
  return (
    <BaseAutocomplete.Item
      data-slot="autocomplete-item"
      className={cn(
        // "grid cursor-default items-center gap-2 py-2 pr-4 pl-2.5 text-sm leading-4 outline-none select-none scroll-my-1",
        // "group-data-[side=none]:min-w-[calc(var(--anchor-width)+1rem)] group-data-[side=none]:pr-12 group-data-[side=none]:text-base group-data-[side=none]:leading-4",
        // "pointer-coarse:py-2.5 pointer-coarse:text-[0.925rem] [@media(hover:hover)]:[&[data-highlighted]]:relative [@media(hover:hover)]:[&[data-highlighted]]:z-0 [@media(hover:hover)]:[&[data-highlighted]]:before:content-[''] [@media(hover:hover)]:[&[data-highlighted]]:before:absolute [@media(hover:hover)]:[&[data-highlighted]]:before:inset-y-0 [@media(hover:hover)]:[&[data-highlighted]]:before:inset-x-1 [@media(hover:hover)]:[&[data-highlighted]]:before:rounded-sm [@media(hover:hover)]:[&[data-highlighted]]:before:z-[-1]",
        // "[@media(hover:hover)]:[&[data-highlighted]]:text-accent-foreground [@media(hover:hover)]:[&[data-highlighted]]:before:bg-input-accent",

        "flex cursor-default select-none outline-none",
        "py-2 pr-8 pl-4 text-base leading-4",
        "bg-popover text-foreground",
        "data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:text-accent-foreground",
        "data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-2 data-[highlighted]:before:inset-y-0",
        "data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded data-[highlighted]:before:bg-input-accent",

        className
      )}
      {...props}
    >
      {children}
    </BaseAutocomplete.Item>
  )
}

// Separator between items
export function AutocompleteSeparator({
  className,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Separator>) {
  return (
    <BaseAutocomplete.Separator
      className={cn(
        "bg-border/30 pointer-events-none mx-auto w-full my-2 h-px",
        className
      )}
      {...props}
    />
  )
}

// Group of items
export function AutocompleteGroup({
  className,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.Group>) {
  return (
    <BaseAutocomplete.Group
      data-slot="autocomplete-group"
      className={className}
      {...props}
    />
  )
}

// Label for group
export function AutocompleteGroupLabel({
  className,
  ...props
}: React.ComponentProps<typeof BaseAutocomplete.GroupLabel>) {
  return (
    <BaseAutocomplete.GroupLabel
      data-slot="autocomplete-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  )
}

// Collection (for virtualization, etc)
export function AutocompleteCollection(
  props: React.ComponentProps<typeof BaseAutocomplete.Collection>
) {
  return <BaseAutocomplete.Collection {...props} />
}

export const FormAutocomplete = {
  Root: AutocompleteRoot,
  Input: AutocompleteInput,
  Trigger: AutocompleteTrigger,
  Icon: AutocompleteIcon,
  Clear: AutocompleteClear,
  Value: AutocompleteValue,
  Portal: AutocompletePortal,
  Backdrop: AutocompleteBackdrop,
  Positioner: AutocompletePositioner,
  Popup: AutocompletePopup,
  Arrow: AutocompleteArrow,
  Status: AutocompleteStatus,
  Empty: AutocompleteEmpty,
  List: AutocompleteList,
  Row: AutocompleteRow,
  Item: AutocompleteItem,
  Separator: AutocompleteSeparator,
  Group: AutocompleteGroup,
  GroupLabel: AutocompleteGroupLabel,
  Collection: AutocompleteCollection,
}
